import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  getRedirectResult,
  deleteUser as firebaseDeleteUser
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { authService } from '../services/firebase/auth.service';
import { badgesService } from '../services/firebase/badges.service';
import { UserProfile } from '../types/auth.types';
import { stripeSyncService } from '../services/subscription/stripe-sync.service';
import { userInitializationService } from '../services/firebase/user-initialization.service';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  deleteUser: () => Promise<void>;
  isRedirectProcessed: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectProcessed, setRedirectProcessed] = useState(false);

  // Fonction pour récupérer le profil utilisateur
  const fetchUserProfile = async (userId: string) => {
    try {
      const profile = await authService.getUserProfile(userId);
      if (profile) {
        setUserProfile(profile);
      } else {
        setUserProfile(null);
      }

      // 🔄 Synchroniser avec Stripe après avoir récupéré le profil
      try {
        const firebaseUser = auth.currentUser;
        if (firebaseUser && firebaseUser.uid === userId) {
          await stripeSyncService.syncSubscriptionOnLogin(firebaseUser);
        }
      } catch (syncError) {
        console.error('❌ [AuthContext] Erreur lors de la synchronisation Stripe:', syncError);
        // Ne pas faire planter l'authentification si la sync Stripe échoue
      }

    } catch (error) {
      console.error('[DEBUG] Erreur lors de la récupération du profil:', error);
      setUserProfile(null);
    }
  };

  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        // Vérifier si une redirection était en cours
        const pendingRedirect = localStorage.getItem('pendingRedirect');
        const redirectAttempted = localStorage.getItem('redirectAttempted');
        const redirectTime = redirectAttempted ? parseInt(redirectAttempted) : 0;
        const currentTime = Date.now();
        const timeSinceRedirect = currentTime - redirectTime;
        
        // Si l'utilisateur est déjà connecté, on peut considérer que la redirection a réussi
        const currentUserObj = auth.currentUser;
        if (currentUserObj) {
          // Récupérer le profil utilisateur
          await fetchUserProfile(currentUserObj.uid);
          
          // Nettoyer les indicateurs de redirection
          localStorage.removeItem('pendingRedirect');
          localStorage.removeItem('redirectAttempted');
          
          setCurrentUser(currentUserObj);
          setRedirectProcessed(true);
          setLoading(false);
          return;
        }
        
        // Si une redirection a été tentée récemment (moins de 5 minutes)
        if ((pendingRedirect || redirectAttempted) && timeSinceRedirect < 300000) {
          // Nettoyer les indicateurs de redirection
          localStorage.removeItem('pendingRedirect');
          localStorage.removeItem('redirectAttempted');
          
          try {
            // Traiter le résultat de la redirection
            const result = await authService.handleRedirectResult();
            
            if (result && result.user) {
              // Récupérer le profil utilisateur
              await fetchUserProfile(result.user.uid);
              
              setCurrentUser(result.user);
              setRedirectProcessed(true);
              
              // Si c'est un nouvel utilisateur, rediriger vers l'onboarding
              if (result.isNewUser) {
                // Logique de redirection vers l'onboarding si nécessaire
              }
            } else {
              // Vérifier une dernière fois si l'utilisateur est connecté
              const firebaseUser = auth.currentUser;
              if (firebaseUser) {
                setCurrentUser(firebaseUser);
                await fetchUserProfile(firebaseUser.uid);
              } else {
                setCurrentUser(null);
                setUserProfile(null);
              }
              
              setRedirectProcessed(true);
            }
          } catch (error) {
            console.error('[DEBUG] Erreur lors du traitement du résultat de redirection:', error);
            
            // Vérifier si l'utilisateur est connecté malgré l'erreur
            const firebaseUser = auth.currentUser;
            if (firebaseUser) {
              setCurrentUser(firebaseUser);
              await fetchUserProfile(firebaseUser.uid);
            } else {
              setCurrentUser(null);
              setUserProfile(null);
            }
            
            setRedirectProcessed(true);
          }
        } else {
          setRedirectProcessed(true);
        }
      } catch (error) {
        console.error('[DEBUG] Erreur lors de la vérification du résultat de redirection:', error);
        
        // Vérifier si l'utilisateur est connecté malgré l'erreur
        if (auth.currentUser) {
          setCurrentUser(auth.currentUser);
          await fetchUserProfile(auth.currentUser.uid);
        } else {
          setCurrentUser(null);
          setUserProfile(null);
        }
        
        setRedirectProcessed(true);
      } finally {
        setLoading(false);
      }
    };
    
    checkRedirectResult();
  }, [redirectProcessed]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        
        // Récupérer le profil utilisateur lors de tout changement d'état d'authentification
        await fetchUserProfile(user.uid);
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      
      if (redirectProcessed) {
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [redirectProcessed]);

  const signIn = async (email: string, password: string) => {
    try {
      // Validation de l'email avant connexion
      const cleanEmail = email.trim();
      if (!cleanEmail) {
        throw new Error('Veuillez saisir une adresse email.');
      }
      
      const result = await signInWithEmailAndPassword(auth, cleanEmail, password);
      
      // Récupérer le profil utilisateur après connexion
      await fetchUserProfile(result.user.uid);
      
      return result;
    } catch (error) {
      console.error('[DEBUG] Erreur de connexion:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      // Validation de l'email avant inscription
      const cleanEmail = email.trim();
      if (!cleanEmail) {
        throw new Error('Veuillez saisir une adresse email.');
      }
      
      const result = await createUserWithEmailAndPassword(auth, cleanEmail, password);
      const profile = await authService._createUserProfile(result.user);
      setUserProfile(profile as UserProfile | null);

      // Initialiser complètement l'utilisateur pour éviter les problèmes de synchronisation Stripe
      try {
        await userInitializationService.initializeUser(result.user.uid, {
          email: result.user.email || '',
          displayName: result.user.displayName || '',
          phoneNumber: result.user.phoneNumber || ''
        });
      } catch (initError) {
        console.error('Erreur lors de l\'initialisation utilisateur (non bloquante):', initError);
        // Ne pas faire échouer l'inscription si l'initialisation échoue
      }

      return result;
    } catch (error) {
      console.error('[DEBUG] Erreur d\'inscription:', error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      await authService.loginWithGoogle();
    } catch (error: any) {
      console.error('[DEBUG] Erreur de connexion Google:', error);
      if (error.code) {
        console.error('[DEBUG] Code d\'erreur:', error.code);
      }
      if (error.message) {
        console.error('[DEBUG] Message d\'erreur:', error.message);
      }
      throw error;
    }
  };

  const signInWithFacebook = async () => {
    try {
      await authService.loginWithFacebook();
    } catch (error: any) {
      console.error('[DEBUG] Erreur de connexion Facebook:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Nettoyer les données utilisateur du localStorage avant la déconnexion
      if (currentUser?.uid) {
        // Nettoyer les clés spécifiques à l'utilisateur
        const keysToRemove = [
          `activeGenericEvent_${currentUser.uid}`,
          `hasSeenWelcomeModal_${currentUser.uid}`,
          `onboardingData_${currentUser.uid}`,
          `userPreferences_${currentUser.uid}`,
          `accessCodeCollaborator_${currentUser.uid}`
        ];
        
        keysToRemove.forEach(key => {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
          }
        });
        
        // Nettoyer aussi les anciennes clés générales pour compatibilité
        const generalKeysToRemove = [
          'activeGenericEvent',
          'onboardingData',
          'accessCodeCollaborator'
        ];
        
        generalKeysToRemove.forEach(key => {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
          }
        });
      }
      
      await firebaseSignOut(auth);
      setUserProfile(null);
    } catch (error) {
      console.error('[DEBUG] Erreur de déconnexion:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('[DEBUG] Erreur de réinitialisation du mot de passe:', error);
      throw error;
    }
  };

  const deleteUser = async () => {
    try {
      if (!currentUser) {
        throw new Error('Aucun utilisateur connecté');
      }

      // Utiliser le service de suppression de compte qui gère les abonnements Stripe
      await authService.deleteAccount();
      setUserProfile(null);
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la suppression du compte:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithFacebook,
    signOut,
    resetPassword,
    deleteUser,
    isRedirectProcessed: redirectProcessed
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
